import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout, { DateInput, SearchableSelect } from './Layout';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDate, getApiUrl, getTodayIso, parseDateToIso } from './config';
import { shareSalesReportPdf } from './saleInvoiceShare';

function Sale() {
    const role = localStorage.getItem("role") || "user";
    const isAdmin = role === "admin";
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const singleVehicleMode = vehicles.length === 1;
    const singleVehicleId = singleVehicleMode ? String(vehicles[0]?.id || "") : "";
    const [vehicleStockByProductId, setVehicleStockByProductId] = useState({});
    const [invoice, setInvoice] = useState({ 
        customer_id: '', 
        vehicle_id: '',
        date: getTodayIso(), 
        total_amount: 0,
        paid_amount: 0,
        payment_mode: 'Cash'
    });
    const [customerName, setCustomerName] = useState('');
    const [prevBalance, setPrevBalance] = useState(0);

    const [items, setItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({ product_id: '', quantity: 0, weight: 0, price: 0, discount_rate: 0, total: 0 });
    const [editingIndex, setEditingIndex] = useState(null);
    const [payments, setPayments] = useState([{ payment_mode: 'Cash', amount: 0 }]);
    const [customerDiscountRate, setCustomerDiscountRate] = useState(0);
    const [saleShareSettings, setSaleShareSettings] = useState({
        whatsappText: true,
        pdf: false,
        pdfDays: 10
    });
    const customerBalanceReqSeq = useRef(0);
    const latestCustomerIdRef = useRef("");

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get('edit');

    const formatWhatsAppNumber = (value) => {
        const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
        if (!digits) return "";
        if (digits.length === 10) return `91${digits}`;
        return digits;
    };

    const openWhatsApp = (phone, message) => {
        const digits = formatWhatsAppNumber(phone);
        const url = digits
            ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
            : `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const buildWhatsAppMessage = (invoiceId, selectedCustomer, selectedVehicle, prevBalanceOverride) => {
        const dateText = formatDate(invoice.date);
        const customerText = selectedCustomer?.name || customerName || '-';
        const paymentLines = (payments || [])
            .map(p => ({ payment_mode: String(p?.payment_mode || "").trim(), amount: Number(p?.amount) || 0 }))
            .filter(p => p.payment_mode && p.amount > 0);
        const modeTotals = paymentLines.reduce((acc, p) => {
            const key = String(p.payment_mode || "").toLowerCase();
            if (key.includes("upi")) acc.upi += p.amount;
            else if (key.includes("bank") || key.includes("transfer")) acc.bank += p.amount;
            else acc.cash += p.amount;
            return acc;
        }, { cash: 0, upi: 0, bank: 0 });
        const cashAmt = Number(modeTotals.cash || 0);
        const upiAmt = Number(modeTotals.upi || 0);

        const itemsLines = items.map((it, idx) => {
            const qty = Number(it.quantity || 0);
            const wt = Number(it.weight || 0);
            const rate = Number(it.price || 0);
            const discRate = Math.max(0, Number(it.discount_rate || 0));
            return [
                `${idx + 1}. ${it.product_name || "-"}`,
                `Qty: ${qty}`,
                `Weight: ${wt.toFixed(2)}`,
                `Rate: ${rate.toFixed(2)}`,
                `Discount: ${discRate.toFixed(2)}`
            ].join("\n");
        });
        const totalText = `₹ ${(Number(invoice.total_amount) || 0).toFixed(2)}`;
        const effectivePrevBalance = Number.isFinite(Number(prevBalanceOverride))
            ? Number(prevBalanceOverride)
            : (Number(prevBalance) || 0);
        const prevText = `₹ ${effectivePrevBalance.toFixed(2)}`;
        const totalAmtText = `₹ ${(effectivePrevBalance + (Number(invoice.total_amount) || 0)).toFixed(2)}`;
        const remainingText = `₹ ${(effectivePrevBalance + (Number(invoice.total_amount) || 0) - (Number(invoice.paid_amount) || 0)).toFixed(2)}`;
        return [
            `Invoice #${invoiceId}`,
            `Date: ${dateText}`,
            `Customer Name: ${customerText}`,
            ...(itemsLines.length > 0 ? itemsLines : ["-"]),
            `Net Amount: ${totalText}`,
            `Previous Amt: ${prevText}`,
            `Total Amount: ${totalAmtText}`,
            `Cash Amt: ₹ ${cashAmt.toFixed(2)}`,
            `UPI Amt: ₹ ${upiAmt.toFixed(2)}`,
            `Balance Amt: ${remainingText}`
        ].join("\n");
    };

    const getPreviousRange = (invoiceDate, days = 10) => {
        const isoDate = parseDateToIso(invoiceDate) || invoiceDate || getTodayIso();
        const base = new Date(`${isoDate}T00:00:00`);
        if (Number.isNaN(base.getTime())) {
            return { from: isoDate, to: isoDate };
        }
        const toLocalIso = (dt) => {
            const y = dt.getFullYear();
            const m = String(dt.getMonth() + 1).padStart(2, "0");
            const d = String(dt.getDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
        };
        const fromDate = new Date(base);
        const safeDays = Math.max(1, Math.min(60, Number(days) || 10));
        fromDate.setDate(base.getDate() - (safeDays - 1));
        const toIso = toLocalIso(base);
        const fromIso = toLocalIso(fromDate);
        return { from: fromIso, to: toIso };
    };

    const handleSalesReportPdfShare = async (customerId, invoiceDate, selectedCustomer, invoiceId) => {
        const { from, to } = getPreviousRange(invoiceDate, saleShareSettings.pdfDays);

        try {
            const reportRes = await axios.get(`${getApiUrl()}/reports/sales`, {
                params: {
                    customer_id: customerId,
                    from,
                    to
                }
            });

            const rows = Array.isArray(reportRes.data) ? reportRes.data : [];
            const result = await shareSalesReportPdf({
                rows,
                customerName: selectedCustomer?.name || customerName || '',
                fromDate: from,
                toDate: to
            });

            if (!result.shared && result.mode === 'download') {
                alert("10 day Sales Report PDF downloaded because direct Android share is not available here.");
            }
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            const detail =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.error ||
                err?.message ||
                "";
            const extra = status ? `Status: ${status}${detail ? `\nDetail: ${detail}` : ""}` : (detail ? `Detail: ${detail}` : "");
            const fallback = window.confirm(
                `Failed to prepare the 10 day Sales Report PDF.${extra ? `\n${extra}` : ""}\nSend WhatsApp text message instead?`
            );
            if (fallback) {
                const message = buildWhatsAppMessage(invoiceId, selectedCustomer, null, prevBalance);
                openWhatsApp(selectedCustomer?.contact, message);
            }
        }
    };

    const fetchPrevBalanceForInvoice = async (customerId, invoiceId, invoiceDate) => {
        const to = parseDateToIso(invoiceDate) || invoiceDate;
        const res = await axios.get(`${getApiUrl()}/reports/sales`, {
            params: { customer_id: customerId, to }
        });
        const rows = res.data || [];
        const hit = rows.find(r => String(r.invoice_id) === String(invoiceId));
        if (!hit) return 0;
        const cumulative = Number(hit.cumulative_balance ?? hit.net_balance ?? 0);
        const total = Number(hit.total_amount || 0);
        const paid = Number(hit.paid_amount || 0);
        return cumulative - (total - paid);
    };

    useEffect(() => {
        const run = async () => {
            try {
                const [prodRes, custRes, vehicleRes] = await Promise.all([
                    axios.get(`${getApiUrl()}/products`),
                    axios.get(`${getApiUrl()}/customers`),
                    axios.get(`${getApiUrl()}/vehicles`)
                ]);
                setProducts(prodRes.data);
                setCustomers(custRes.data);
                setVehicles(vehicleRes.data);

                try {
                    const settingsRes = await axios.get(`${getApiUrl()}/firm-settings`);
                    const s = settingsRes?.data || {};
                    setSaleShareSettings({
                        whatsappText: Number(s.sale_share_whatsapp_text) ? true : false,
                        pdf: Number(s.sale_share_pdf) ? true : false,
                        pdfDays: Math.max(1, Math.min(60, Number(s.sale_share_pdf_days) || 10))
                    });
                } catch (e) {
                    console.error(e);
                }

                if (editId) {
                    if (!isAdmin) {
                        alert("Only admin can update records");
                        navigate("/sale");
                        return;
                    }
                    const res = await axios.get(`${getApiUrl()}/sales/${editId}`);
                    const data = res.data;
                    const invoiceDate = String(data.date || "").slice(0, 10) || getTodayIso();
                    setInvoice({
                        customer_id: data.customer_id,
                        vehicle_id: data.vehicle_id || '',
                        date: invoiceDate,
                        total_amount: Number(data.total_amount || 0),
                        paid_amount: Number(data.paid_amount || 0),
                        payment_mode: data.payment_mode || 'Cash'
                    });
                    const prevFromApi = Number(data.prev_balance);
                    if (Number.isFinite(prevFromApi)) {
                        setPrevBalance(prevFromApi);
                    } else {
                        try {
                            const prev = await fetchPrevBalanceForInvoice(data.customer_id, editId, invoiceDate);
                            setPrevBalance(prev);
                        } catch (e) {
                            console.error(e);
                            try {
                                await fetchCustomerBalance(data.customer_id);
                            } catch {
                                setPrevBalance(0);
                            }
                        }
                    }
                    if (Array.isArray(data.payments) && data.payments.length > 0) {
                        setPayments(data.payments.map(p => ({ payment_mode: p.payment_mode || 'Cash', amount: p.amount || 0 })));
                    } else {
                        setPayments([{ payment_mode: data.payment_mode || 'Cash', amount: data.paid_amount || 0 }]);
                    }
                    setItems(data.items.map(item => ({
                        product_id: item.product_id,
                        product_name: item.product_name,
                        quantity: item.quantity,
                        weight: parseFloat(item.weight) || 0,
                        price: item.price,
                        discount_rate: parseFloat(item.discount_rate) || 0,
                        total: item.total
                    })));
                    const initDisc = parseFloat(data?.items?.[0]?.discount_rate) || 0;
                    setCustomerDiscountRate(initDisc);
                    setCurrentItem(prev => ({ ...prev, discount_rate: initDisc }));
                    fetchVehicleStock(data.vehicle_id);
                }
            } catch (err) {
                console.error(err);
            }
        };
        run();
    }, [editId, isAdmin, navigate]);

    useEffect(() => {
        if (singleVehicleMode && singleVehicleId) {
            setInvoice(prev => (prev.vehicle_id ? prev : { ...prev, vehicle_id: singleVehicleId }));
            fetchVehicleStock(singleVehicleId);
            return;
        }
        if (invoice.vehicle_id) fetchVehicleStock(invoice.vehicle_id);
    }, [invoice.vehicle_id, singleVehicleMode, singleVehicleId]);

    useEffect(() => {
        if (invoice.customer_id && !editId) {
            latestCustomerIdRef.current = String(invoice.customer_id || "");
            fetchCustomerBalance(invoice.customer_id);
        } else if (!invoice.customer_id) {
            latestCustomerIdRef.current = "";
            setPrevBalance(0);
        }
    }, [invoice.customer_id, editId]);

    useEffect(() => {
        if (!invoice.customer_id) {
            if (customerName !== '') setCustomerName('');
            return;
        }
        const customer = customers.find(c => c.id === parseInt(invoice.customer_id));
        const nextName = customer ? (customer.name || '') : '';
        if (customerName !== nextName) setCustomerName(nextName);
    }, [invoice.customer_id, customers, customerName]);

    useEffect(() => {
        if (editId) return;
        if (!invoice.customer_id) {
            setCustomerDiscountRate(0);
            setCurrentItem(prev => ({ ...prev, discount_rate: 0 }));
            return;
        }
        const customer = customers.find(c => c.id === parseInt(invoice.customer_id));
        const rate = customer ? (parseFloat(customer.discount_rate) || 0) : 0;
        setCustomerDiscountRate(rate);
        setCurrentItem(prev => ({ ...prev, discount_rate: rate }));
    }, [invoice.customer_id, customers, editId]);

    const fetchCustomerBalance = async (id) => {
        const requestedCustomerId = String(id || "");
        const seq = ++customerBalanceReqSeq.current;
        try {
            const res = await axios.get(`${getApiUrl()}/customers/${id}/balance`);
            if (seq !== customerBalanceReqSeq.current) return;
            if (requestedCustomerId && latestCustomerIdRef.current && requestedCustomerId !== latestCustomerIdRef.current) return;
            setPrevBalance(Number(res.data.balance) || 0);
        } catch (err) {
            console.error(err);
            if (seq !== customerBalanceReqSeq.current) return;
            if (requestedCustomerId && latestCustomerIdRef.current && requestedCustomerId !== latestCustomerIdRef.current) return;
            setPrevBalance(0);
        }
    };

    const fetchVehicleStock = async (vehicleId) => {
        if (!vehicleId) {
            setVehicleStockByProductId({});
            return;
        }
        try {
            const res = await axios.get(`${getApiUrl()}/vehicle-stock/${vehicleId}`);
            const map = {};
            (res.data || []).forEach(r => {
                map[r.product_id] = { quantity: Number(r.quantity || 0), weight: Number(r.weight || 0) };
            });
            setVehicleStockByProductId(map);
        } catch (err) {
            console.error(err);
            setVehicleStockByProductId({});
        }
    };

    const addItem = () => {
        const effectiveVehicleId = invoice.vehicle_id || (singleVehicleMode ? singleVehicleId : "");
        if (!effectiveVehicleId) {
            alert("Select Vehicle first");
            return;
        }
        const weight = parseFloat(currentItem.weight) || 0;
        if (!currentItem.product_id || weight <= 0) return;
        const product = products.find(p => p.id === parseInt(currentItem.product_id));
        const baseStock = vehicleStockByProductId[parseInt(currentItem.product_id)] || { quantity: 0, weight: 0 };
        const oldItem = editingIndex !== null ? items[editingIndex] : null;
        const sameProductEdit = !!oldItem && String(oldItem.product_id) === String(currentItem.product_id);
        const availableQty = sameProductEdit ? (Number(baseStock.quantity) + (Number(oldItem.quantity) || 0)) : Number(baseStock.quantity);
        const availableWeight = sameProductEdit ? (Number(baseStock.weight) + (Number(oldItem.weight) || 0)) : Number(baseStock.weight);
        if ((parseInt(currentItem.quantity) || 0) > availableQty) {
            alert(`Insufficient ${singleVehicleMode ? "godown" : "vehicle"} quantity! Available: ${availableQty}`);
            return;
        }
        if (weight > availableWeight) {
            alert(`Insufficient ${singleVehicleMode ? "godown" : "vehicle"} weight! Available: ${availableWeight}`);
            return;
        }

        const newItem = { ...currentItem, discount_rate: customerDiscountRate, product_name: product?.name || "" };
        setItems(prev => {
            if (editingIndex === null) return [...prev, newItem];
            return prev.map((it, idx) => (idx === editingIndex ? newItem : it));
        });
        setEditingIndex(null);
        setCurrentItem({ product_id: '', quantity: 0, weight: 0, price: 0, discount_rate: 0, total: 0 });
    };

    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
        setEditingIndex(prev => {
            if (prev === null) return prev;
            if (prev === index) return null;
            if (prev > index) return prev - 1;
            return prev;
        });
        if (editingIndex === index) {
            setCurrentItem({ product_id: '', quantity: 0, weight: 0, price: 0, discount_rate: 0, total: 0 });
        }
    };

    const startEditItem = (index) => {
        const it = items[index];
        if (!it) return;
        setEditingIndex(index);
        setCurrentItem({
            product_id: it.product_id,
            quantity: Number(it.quantity) || 0,
            weight: Number(it.weight) || 0,
            price: Number(it.price) || 0,
            discount_rate: Number(it.discount_rate) || 0,
            total: Number(it.total) || 0
        });
    };

    useEffect(() => {
        const netTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        setInvoice(prev => (prev.total_amount === netTotal ? prev : { ...prev, total_amount: netTotal }));
    }, [items]);

    useEffect(() => {
        const discountRate = Math.max(0, parseFloat(customerDiscountRate) || 0);
        setItems(prev => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            let changed = false;
            const next = prev.map(it => {
                const weight = Number(it.weight) || 0;
                const price = Number(it.price) || 0;
                const effectiveRate = Math.max(0, price - discountRate);
                const total = Math.max(0, Number((weight * effectiveRate).toFixed(2)));
                const prevDisc = Math.max(0, Number(it.discount_rate) || 0);
                const prevTotal = Number(it.total) || 0;
                if (prevDisc !== discountRate || prevTotal !== total) changed = true;
                return { ...it, discount_rate: discountRate, total };
            });
            return changed ? next : prev;
        });
    }, [customerDiscountRate]);

    useEffect(() => {
        const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        setInvoice(prev => (prev.paid_amount === totalPaid ? prev : { ...prev, paid_amount: totalPaid }));
    }, [payments]);

    const handleSale = async (e) => {
        e.preventDefault();
        if (!invoice.customer_id) {
            alert("Select a valid customer");
            return;
        }
        if (items.length === 0) {
            alert("Add at least one item");
            return;
        }
        
        const selectedCustomer = customers.find(c => c.id === parseInt(invoice.customer_id));
        const payload = {
            ...invoice,
            date: parseDateToIso(invoice.date) || invoice.date,
            vehicle_id: invoice.vehicle_id || (singleVehicleMode ? singleVehicleId : invoice.vehicle_id),
            paid_amount: parseFloat(invoice.paid_amount) || 0,
            payments: payments
                .map(p => ({ payment_mode: p.payment_mode, amount: parseFloat(p.amount) || 0 }))
                .filter(p => p.payment_mode && p.amount > 0),
            items
        };
        
        try {
            if (editId) {
                if (!isAdmin) return alert("Only admin can update records");
                await axios.put(`${getApiUrl()}/sales/${editId}`, payload);
                alert('Sale Invoice Updated');
                if (saleShareSettings.pdf) {
                    await handleSalesReportPdfShare(payload.customer_id, payload.date, selectedCustomer, editId);
                }
                if (saleShareSettings.whatsappText) {
                    const ok = window.confirm("Send WhatsApp text message?");
                    if (ok) {
                        const message = buildWhatsAppMessage(editId, selectedCustomer, null, prevBalance);
                        openWhatsApp(selectedCustomer?.contact, message);
                    }
                }
                navigate('/reports/sale');
            } else {
                const res = await axios.post(`${getApiUrl()}/sales`, payload);
                const newId = res?.data?.id || "";
                alert('Sale Invoice Recorded');
                if (saleShareSettings.pdf) {
                    await handleSalesReportPdfShare(payload.customer_id, payload.date, selectedCustomer, newId);
                }
                if (saleShareSettings.whatsappText) {
                    const ok = window.confirm("Send WhatsApp text message?");
                    if (ok) {
                        const message = buildWhatsAppMessage(newId, selectedCustomer, null, prevBalance);
                        openWhatsApp(selectedCustomer?.contact, message);
                    }
                }
                setItems([]);
                setInvoice({ 
                    customer_id: '', 
                    vehicle_id: singleVehicleMode ? singleVehicleId : '',
                    date: getTodayIso(), 
                    total_amount: 0,
                    paid_amount: 0,
                    payment_mode: 'Cash'
                });
                setCustomerName('');
                setPayments([{ payment_mode: 'Cash', amount: 0 }]);
                setVehicleStockByProductId({});
            }
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to save sale invoice";
            alert(message);
        }
    };

    useEffect(() => {
        const weight = parseFloat(currentItem.weight) || 0;
        const rate = parseFloat(currentItem.price) || 0;
        const discountRate = Math.max(0, parseFloat(customerDiscountRate) || 0);
        const effectiveRate = Math.max(0, rate - discountRate);
        const total = Math.max(0, weight * effectiveRate);
        setCurrentItem(prev => ({
            ...prev,
            discount_rate: discountRate,
            total
        }));
    }, [currentItem.weight, currentItem.price, customerDiscountRate]);

    return (
        <Layout>
            <div className="container mt-4">
                <h2>{editId ? `Edit Sale Invoice #${editId}` : 'New Sale Invoice'}</h2>
                <div className="card p-4 mb-4 shadow-sm border-0">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label fw-bold">Customer</label>
                            <SearchableSelect
                                value={invoice.customer_id}
                                options={customers.map(c => ({ value: String(c.id), label: c.name || '' }))}
                                placeholder="Type customer name..."
                                required
                                onChange={(nextId, opt) => {
                                    setInvoice(prev => ({ ...prev, customer_id: nextId }));
                                    setCustomerName(opt?.label || '');
                                }}
                                allowClear
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">Vehicle No</label>
                            <select className="form-select" value={invoice.vehicle_id} onChange={e => setInvoice({ ...invoice, vehicle_id: e.target.value })} disabled={singleVehicleMode}>
                                <option value="">Select Vehicle</option>
                                {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_no}</option>)}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">Date</label>
                            <DateInput value={invoice.date} onChange={(v) => setInvoice(prev => ({ ...prev, date: v }))} required />
                        </div>
                    </div>
                </div>

                <div className="card p-4 mb-4 bg-light">
                    <h5>Add Items</h5>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Product</label>
                            <select className="form-select" value={currentItem.product_id} onChange={e => {
                                const prod = products.find(p => p.id === parseInt(e.target.value));
                                setCurrentItem({...currentItem, product_id: e.target.value, price: prod ? prod.price : 0, discount_rate: customerDiscountRate});
                            }}>
                                <option value="">Select Product</option>
                                {products.map(p => {
                                    const vs = vehicleStockByProductId[p.id] || { quantity: 0, weight: 0 };
                                    return (
                                        <option key={p.id} value={p.id}>
                                            {p.name} ({singleVehicleMode ? "GQty" : "VQty"}: {vs.quantity}, {singleVehicleMode ? "GWt" : "VWt"}: {vs.weight.toFixed(2)})
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Quantity</label>
                            <input type="number" className="form-control" value={currentItem.quantity} onChange={e => setCurrentItem({...currentItem, quantity: parseInt(e.target.value)})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Weight</label>
                            <input type="number" className="form-control" value={currentItem.weight} onChange={e => setCurrentItem({...currentItem, weight: parseFloat(e.target.value)})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Rate / Kg</label>
                            <input type="number" className="form-control" value={currentItem.price} onChange={e => setCurrentItem({...currentItem, price: parseFloat(e.target.value)})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Disc Rs/Kg</label>
                            <input
                                type="number"
                                className="form-control"
                                value={customerDiscountRate}
                                onChange={(e) => setCustomerDiscountRate(parseFloat(e.target.value) || 0)}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Total</label>
                            <input type="number" className="form-control" value={currentItem.total} readOnly />
                        </div>
                        <div className="col-12 d-grid d-md-flex justify-content-end">
                            <button type="button" className="btn btn-primary" onClick={addItem}>
                                {editingIndex !== null ? "Update Item" : "Add Item"}
                            </button>
                            {editingIndex !== null && (
                                <button
                                    type="button"
                                    className="btn btn-secondary ms-md-2 mt-2 mt-md-0"
                                    onClick={() => {
                                        setEditingIndex(null);
                                        setCurrentItem({ product_id: '', quantity: 0, weight: 0, price: 0, discount_rate: 0, total: 0 });
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="table-responsive mb-4">
                    <table className="table table-bordered">
                        <thead className="table-dark">
                            <tr>
                                <th>Product</th>
                                <th>Qty</th>
                                <th>Weight</th>
                                <th>Rate / Kg</th>
                                <th>Disc Rs/Kg</th>
                                <th>Total</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.product_name}</td>
                                    <td>{item.quantity}</td>
                                    <td>{parseFloat(item.weight || 0).toFixed(2)}</td>
                                    <td>{item.price}</td>
                                    <td>{parseFloat(item.discount_rate || 0).toFixed(2)}</td>
                                    <td>{item.total}</td>
                                    <td>
                                        <button className="btn btn-warning btn-sm me-1" type="button" onClick={() => startEditItem(index)}>Edit</button>
                                        <button className="btn btn-danger btn-sm" type="button" onClick={() => removeItem(index)}>Remove</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colSpan="6" className="text-end">Gross Total</th>
                                <th colSpan="2">₹ {invoice.total_amount.toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="6" className="text-end">Previous Balance</th>
                                <th colSpan="2">₹ {prevBalance.toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="6" className="text-end">Net Invoice Total</th>
                                <th colSpan="2">₹ {invoice.total_amount.toFixed(2)}</th>
                            </tr>
                            <tr className="table-secondary">
                                <th colSpan="6" className="text-end fw-bold">Grand Total (Total Balance)</th>
                                <th colSpan="2" className="fw-bold">₹ {(prevBalance + invoice.total_amount).toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="6" className="text-end text-success align-middle">Amount Paid</th>
                                <th colSpan="2">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Mode</th>
                                                    <th>Amount</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payments.map((p, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={p.payment_mode}
                                                                onChange={e => {
                                                                    const next = [...payments];
                                                                    next[idx] = { ...next[idx], payment_mode: e.target.value };
                                                                    setPayments(next);
                                                                }}
                                                            >
                                                                <option value="Cash">Cash</option>
                                                                <option value="UPI">UPI</option>
                                                                <option value="Bank Transfer">Bank Transfer</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={p.amount}
                                                                onChange={e => {
                                                                    const next = [...payments];
                                                                    next[idx] = { ...next[idx], amount: e.target.value };
                                                                    setPayments(next);
                                                                }}
                                                                onFocus={(e) => e.target.select()}
                                                            />
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => setPayments(payments.filter((_, i) => i !== idx))}
                                                                disabled={payments.length === 1}
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm mt-2"
                                        onClick={() => setPayments([...payments, { payment_mode: 'Cash', amount: 0 }])}
                                    >
                                        Add Mode
                                    </button>
                                </th>
                            </tr>
                            <tr>
                                <th colSpan="6" className="text-end text-danger fw-bold">Remaining Balance</th>
                                <th colSpan="2" className="fw-bold">₹ {(prevBalance + invoice.total_amount - invoice.paid_amount).toFixed(2)}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="text-end mb-5">
                    <button className="btn btn-primary btn-lg w-100 w-md-auto" onClick={handleSale}>{editId ? 'Update Invoice' : 'Save Invoice'}</button>
                    {editId && <button className="btn btn-secondary btn-lg w-100 w-md-auto ms-md-2 mt-2 mt-md-0" onClick={() => navigate('/reports/sale')}>Cancel</button>}
                </div>
            </div>
        </Layout>
    );
}

export default Sale;
